import { TextField } from '@atgfr8/atg-ui-tailwind';
import mapConfig from '@components/Map/mapConfig';
import { Loader } from '@googlemaps/js-api-loader';
import { JSXElement, onCleanup, onMount, Setter } from 'solid-js';

export type AddressDetails = {
  address1?: string;
  city?: string;
  county?: string;
  state?: string;
  zipCode?: string;
  country?: string;
};

interface AutocompleteAddressProps {
  label: string | JSXElement;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onItemSelect?: (item: AddressDetails) => void;
  error?: string | string[] | null;
  classes?: string;
  zIndex?: string;
  disabled?: boolean;
  maxLength?: number;
  onBlur?: (e: FocusEvent) => void;
  required?: boolean;
}

const AutocompleteAddress = (props: AutocompleteAddressProps) => {
  let inputRef: HTMLInputElement | undefined;

  const adjustZIndex = () => {
    if (!Boolean(props.zIndex)) return;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (
            node.nodeType === 1 &&
            (node as Element).classList.contains('pac-container')
          ) {
            (node as HTMLElement).style.zIndex = props.zIndex!;
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    onCleanup(() => observer.disconnect());
  };

  const getComp = (
    comps: google.maps.GeocoderAddressComponent[],
    type: string,
  ) => comps.find((c) => c.types.includes(type));

  const pickCity = (
    comps: google.maps.GeocoderAddressComponent[],
  ): string | undefined => {
    const locality = getComp(comps, 'locality');
    const admin3 = getComp(comps, 'administrative_area_level_3');
    const subloc1 =
      getComp(comps, 'sublocality_level_1') || getComp(comps, 'sublocality');
    const admin2 = getComp(comps, 'administrative_area_level_2');

    return (
      (locality?.long_name ?? '') ||
      (admin3?.long_name ?? '') ||
      (subloc1?.long_name ?? '') ||
      (admin2?.long_name ?? '') ||
      undefined
    );
  };

  onMount(async () => {
    const loader = new Loader({
      apiKey: mapConfig.GoogleGeoCode.GoogleGeoCodeAPIKey,
      version: 'weekly',
      libraries: ['places'],
    });

    await loader.load();
    adjustZIndex();

    if (inputRef) {
      const autocomplete = new google.maps.places.Autocomplete(inputRef, {
        types: ['address'],
        componentRestrictions: { country: ['US', 'CA', 'MX'] },
        fields: ['address_components', 'geometry', 'name', 'formatted_address'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const comps = place.address_components || [];
        if (!comps.length) return;

        const streetNo = getComp(comps, 'street_number')?.short_name;
        const route = getComp(comps, 'route')?.short_name;
        const admin1 = getComp(comps, 'administrative_area_level_1');
        const admin2 = getComp(comps, 'administrative_area_level_2');
        const postal = getComp(comps, 'postal_code');
        const country = getComp(comps, 'country');

        const address: AddressDetails = {
          address1:
            [streetNo, route].filter(Boolean).join(' ').trim() || undefined,
          city: pickCity(comps),
          county:
            (admin2?.short_name ?? '') ||
            (admin2?.long_name ?? '') ||
            undefined,
          state:
            (admin1?.short_name ?? '') ||
            (admin1?.long_name ?? '') ||
            undefined,
          zipCode:
            (postal?.long_name ?? '') ||
            (postal?.short_name ?? '') ||
            undefined,
          country: (country?.long_name ?? '') || undefined,
        };
        props.onItemSelect?.(address);
      });
    }
  });
  onCleanup(() => {
    if (
      inputRef !== undefined &&
      Boolean(window.google) &&
      Boolean(window.google.maps) &&
      Boolean(window.google.maps.event)
    ) {
      window.google.maps.event.clearInstanceListeners(inputRef);
    }
  });

  return (
    <TextField
      id="address"
      dataTestId={`address-autocomplete-${String(props.label).replace(/\s+/g, '-')}-textInput`}
      label={props.label}
      required={props.required}
      ref={
        ((input: HTMLInputElement) => (inputRef = input)) as Setter<
          HTMLInputElement | undefined
        >
      }
      value={props.value}
      onChange={(value: string) => props.onChange?.(value)}
      placeholder={props.placeholder}
      disabled={props.disabled}
      error={props.error}
      maxLength={props.maxLength}
      on:blur={props.onBlur}
    />
  );
};

export default AutocompleteAddress;
